import { useTranslation } from 'react-i18next';

import { OpportunityDetails } from '@/features/opportunities/types';
import { useSaleOptions } from '@/hooks';
import { getRangeOfPercentage } from '../../../helpers';
import { Button, Icon, Typography } from '@/components';

export type SaleProcessBoxProps = {
  readonly opportunity: OpportunityDetails;
  readonly onEdit?: () => void;
  readonly isEditable: boolean;
};

export function SaleProcessBox({ opportunity, onEdit, isEditable }: SaleProcessBoxProps) {
  const { t } = useTranslation(['opportunities']);
  const { map: mapSaleOption } = useSaleOptions();
  const saleOptions = opportunity.options.map(mapSaleOption).join(', ');

  const fields = [
    {
      label: t('Percentage on Sale'),
      value: getRangeOfPercentage(opportunity.percentage.minimum, opportunity.percentage.maximum),
    },
    {
      label: t('Deal Type'),
      value: saleOptions,
    },
    {
      label: t('Process Comments'),
      value: (
        <span className="text-base font-normal whitespace-pre-wrap text-neutral-900">
          {opportunity.comments ?? t('No comments available')}
        </span>
      ),
    },
  ];

  return (
    <div className="flex">
      <div className="flex-1 rounded-lg border border-neutral-50 bg-white p-6 shadow-xs">
        <div className="mb-3 flex items-center">
          <div className="mr-2 flex h-10 w-10 items-center justify-center rounded-full bg-neutral-50">
            <Icon name="handshake" className="h-6 w-6" />
          </div>
          <Typography as="h3" weight="semibold" size="title">
            {t('Process Details')}
          </Typography>
          {isEditable && (
            <Button size="sm" color="neutral" icon="edit" onClick={onEdit} className="ml-auto">
              {t('Edit')}
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1">
          {fields.map(({ label, value }) => (
            <Typography key={label} as="p" size="body-2" weight="semibold" relaxed className="mb-1">
              {label}: <span className="ml-3 text-base font-normal text-neutral-900">{value}</span>
            </Typography>
          ))}
        </div>
      </div>
    </div>
  );
}
